import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Inbox, UserPlus, ExternalLink } from "lucide-react";
import { API } from "@/contexts/AuthContext";
import { StatusBadge } from "./StaffDashboard";

export default function ReferralsList() {
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [converting, setConverting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/referrals`);
      setReferrals(data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const convert = async (refId) => {
    if (!window.confirm("Convert this referral into a client record assigned to you?")) return;
    setConverting(refId);
    try {
      const { data } = await axios.post(`${API}/referrals/${refId}/convert`);
      navigate(`/staff/clients/${data.id}`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't convert. Please try again.");
    } finally {
      setConverting(null);
    }
  };

  return (
    <div data-testid="referrals-page">
      <div className="mb-8">
        <div className="text-sm uppercase tracking-wider text-[#EA823A] font-semibold mb-2">Inbox</div>
        <h1 className="font-display text-4xl text-[#1F2A2E]">Referrals</h1>
        <p className="text-[#4A595E] mt-2">Submissions from the public referral form.</p>
      </div>

      {loading ? (
        <div className="text-center py-20"><div className="w-8 h-8 border-3 border-[#E2718A] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : referrals.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-[#F0E5DE]" data-testid="referrals-empty">
          <Inbox className="w-12 h-12 mx-auto text-[#F4C3CC] mb-4" />
          <h3 className="font-display text-xl text-[#1F2A2E] mb-2">No referrals yet</h3>
          <p className="text-[#6B7A80] text-sm">When someone submits the public referral form, it'll appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {referrals.map((r) => (
            <div key={r.id} className="bg-white rounded-3xl border border-[#F0E5DE] overflow-hidden" data-testid={`referral-${r.id}`}>
              <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="w-full text-left p-5 flex items-center justify-between hover:bg-[#F1E4D5]/50 transition">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="font-display text-lg text-[#1F2A2E]">{r.first_name} {r.last_name}</div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="text-xs text-[#6B7A80]">
                    {r.ndis_number && <span>NDIS {r.ndis_number} · </span>}
                    {r.primary_disability} · Received {new Date(r.created_at).toLocaleDateString("en-AU")}
                  </div>
                </div>
                <ExternalLink className={`w-4 h-4 text-[#6B7A80] transition ${expanded === r.id ? "rotate-90" : ""}`} />
              </button>

              {expanded === r.id && (
                <div className="px-5 pb-5 border-t border-[#F0E5DE] pt-5 space-y-4" data-testid={`referral-detail-${r.id}`}>
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <Field label="Phone" value={r.phone} />
                    <Field label="Email" value={r.email} />
                    <Field label="DOB" value={r.date_of_birth} />
                    <Field label="Gender" value={r.gender} />
                    <Field label="Identifies ATSI" value={r.identifies_atsi} />
                    <Field label="Sexual identity" value={r.sexual_identity} />
                    <Field label="Plan dates" value={r.plan_start_date && r.plan_end_date ? `${r.plan_start_date} to ${r.plan_end_date}` : ""} />
                  </div>
                  <Field label="Address" value={r.address} block />
                  <Field label="Reason for referral" value={r.reason_for_referral} block />
                  <Field label="Representative" value={r.representative_details} block />
                  {r.services_requested?.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">Services requested</div>
                      <div className="flex flex-wrap gap-2">
                        {r.services_requested.map((s) => (
                          <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-[#F1E4D5] border border-[#F0E5DE] text-[#284952]">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="pt-4 border-t border-[#F0E5DE] flex flex-wrap items-center gap-3">
                    {r.status === "converted" ? (
                      <button onClick={() => r.converted_client_id && navigate(`/staff/clients/${r.converted_client_id}`)} className="btn-outline text-sm">
                        Open client record
                      </button>
                    ) : (
                      <button onClick={() => convert(r.id)} disabled={converting === r.id} className="btn-primary text-sm" data-testid={`convert-${r.id}`}>
                        <UserPlus className="w-4 h-4" /> {converting === r.id ? "Converting..." : "Convert to client"}
                      </button>
                    )}
                    {r.email && <a href={`mailto:${r.email}`} className="text-sm text-[#284952] hover:text-[#E2718A]">Email participant →</a>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, block }) {
  if (!value) return null;
  return (
    <div className={block ? "" : ""}>
      <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1">{label}</div>
      <div className="text-sm text-[#1F2A2E] leading-relaxed">{value}</div>
    </div>
  );
}
